import React, { useState, useEffect, useRef } from "react";
import PopSnackbar from "../../../components/snackbar";
import {
  DownloadVideo,
  GetVideoInfo,
  UpdateVideo,
  DeleteVideoGlobal,
} from "../../../services/video";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import StyledDropzone from "../../../components/dropzone/dropzone";
import VideoGeneralTab from "../tabs/general";
import CategoriesVideoTab from "../tabs/categories";
import TagVideoTab from "../tabs/tags";
import { GetTags } from "../../../services/tag";

import { CircularProgress } from "@mui/material";
import { GetAllPartners } from "../../../services/partners";
import { GetAllLocations } from "../../../services/locations";
import { GetAllCategories } from "../../../services/category";
import { getFinalCategoriesWithFirstAndLast } from "../../../components/getfinalcategoriesfirstandlast";
import AlertModal from "../../../components/modal";

const EditVideo = () => {
  const [preview, setPreview] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // General video states
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDuration, setVideoDuration] = useState("");
  const [isLandscape, setIsLandscape] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [frequency, setFrequency] = useState("");
  // Tabs and notifications
  const [activeTab, setActiveTab] = useState("Informatii generale");
  const [toastStatus, setToastStatus] = useState(false);
  const [toastDescription, setToastDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Categories and locations states
  const [categories, setCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [groups, setGroups] = useState([
    { id: 0, categoryValues: [], locationValues: [], selectedPartner: null },
  ]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCategoryGroups, setSelectedCategoryGroups] = useState([]);

  // Tags
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  // Loaders
  const [loading, setLoading] = useState(true);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Additional flags
  const [isSpecialCategory, setIsSpecialCategory] = useState(false);
  const [allPartners, setAllPartners] = useState(false);
  const [allLocations, setAllLocations] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  const videoUrlRef = useRef(null);

  const handleModalClose = () => {
    setModalOpen(false);
  };

  // Fetch categories, locations, partners, and tags
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await GetAllCategories();
      setAvailableCategories(response.data);
      return response.data;
    } catch (error) {
      handleStatus(
        "Failed to fetch categories.",
        setToastStatus,
        setToastDescription
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await GetAllLocations();
      setAvailableLocations(response.data);
    } catch (error) {
      handleStatus(
        "Failed to fetch locations.",
        setToastStatus,
        setToastDescription
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await GetAllPartners();
      setAvailablePartners(response.data);
    } catch (error) {
      handleStatus(
        "Failed to fetch partners.",
        setToastStatus,
        setToastDescription
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      setIsTagsLoading(true);
      const response = await GetTags();
      setAvailableTags(response.data);
    } catch (error) {
      handleStatus(
        "Failed to fetch tags.",
        setToastStatus,
        setToastDescription
      );
    } finally {
      setIsTagsLoading(false);
    }
  };

  const findCategoryPath = (categories, targetId, path = []) => {
    for (const category of categories) {
      if (category.ID === targetId) {
        return [...path, category.ID];
      }
      if (category.subcategories?.length) {
        const subPath = findCategoryPath(category.subcategories, targetId, [
          ...path,
          category.ID,
        ]);
        if (subPath) return subPath;
      }
    }
    return null;
  };

  const onSubmitClick = () => {
    console.log("onSubmitClick triggered");

    if (partners.length === 1 && !modalOpen) {
      console.log("Opening modal...");
      setModalOpen(true);
      return;
    }

    handleSubmit();
  };

  const fetchVideoInfo = async (categoriesData) => {
    try {
      setLoading(true);

      const content = await GetVideoInfo(id);
      if (content.status !== "OK") {
        throw new Error(content.error || "Something went wrong.");
      }
      const data = content.data;

      // Set video basic info
      setVideoTitle(data.title);
      setVideoDuration(data.duration);
      setIsLandscape(data.is_landscape);
      if (data.frequency) {
        setFrequency(data.frequency);
      }

      // Preprocess available categories to include displayName
      const finalCategories =
        getFinalCategoriesWithFirstAndLast(categoriesData);

      // Map selected categories to include displayName
      const updatedCategories = data.categories.map((selectedCategory) => {
        const matchedCategory = finalCategories.find(
          (cat) => cat.ID === selectedCategory.ID
        );
        return matchedCategory || selectedCategory;
      });
      setCategories(updatedCategories);

      const assignedCategoryPaths = updatedCategories.map((category) =>
        findCategoryPath(categoriesData, category.ID)
      );
      setSelectedCategoryGroups(assignedCategoryPaths);

      const isSpecialCategory = updatedCategories.some(
        (category) => category.ID === 2
      );
      setIsSpecialCategory(isSpecialCategory);

      let allPartnerIds = [];
      let allLocationIds = [];
      if (isSpecialCategory) {
        const partnersData = await fetchPartners();
        const locationsData = await fetchLocations();
        allPartnerIds = partnersData?.map((partner) => partner.ID);
        allLocationIds = locationsData?.map((location) => location.ID);
      }

      const initialGroups = assignedCategoryPaths.map(
        (categoryValues, index) => {
          const lastCategoryId = categoryValues[categoryValues.length - 1];
          if (lastCategoryId === 2) {
            const selectedPartners = Array.from(
              new Set(data.locations?.map((loc) => loc.partner_id))
            );
            const allPartnersSelected =
              selectedPartners?.length === allPartnerIds?.length;
            return {
              id: index,
              categoryValues,
              locationValues: allPartnersSelected
                ? allLocationIds
                : data.locations?.map((loc) => loc.ID) || [],
              selectedPartner: selectedPartners,
            };
          }
          return {
            id: index,
            categoryValues,
            locationValues: [],
            selectedPartner: [],
          };
        }
      );
      setGroups(initialGroups);

      setTags(data.tags);
      setLocations(data.locations);
      const uniquePartners = Array.from(
        new Map(
          data.locations
            .filter((location) => location.partner)
            .map((location) => [location.partner.ID, location.partner])
        ).values()
      );

      setPartners(uniquePartners);

      // Use SAS URL directly for video preview
      const downloadResponse = await DownloadVideo(id);

      // Instead of checking status, verify that a URL is returned.
      if (!downloadResponse || !downloadResponse.url) {
        throw new Error(
          downloadResponse && downloadResponse.error
            ? downloadResponse.error
            : "Failed to download video."
        );
      }
      const { url: sasUrl, title: downloadTitle } = downloadResponse;

      // Clean up any previous object URL if it exists
      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
      }
      videoUrlRef.current = sasUrl;
      setVideoPreview(sasUrl);

      // Set videoFile to the SAS URL so that the submit button is enabled
      setVideoFile(sasUrl);

      // Optionally update the video title from the download response if provided
      if (downloadTitle) {
        setVideoTitle(downloadTitle);
      }
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoading(false);
    }
  };

  // Video processing
  const processVideo = (file) => {
    if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
    const videoUrl = URL.createObjectURL(file);
    videoUrlRef.current = videoUrl;
    setVideoFile(file);
    setVideoPreview(videoUrl);
    setVideoTitle(file.name.split(".").slice(0, -1).join("."));
    const videoElement = document.createElement("video");
    videoElement.src = videoUrl;
    videoElement.onloadedmetadata = () =>
      setVideoDuration(Math.floor(videoElement.duration));
  };

  const handleFileChange = (event) => {
    const file = Array.isArray(event) ? event[0] : event.target?.files?.[0];
    if (file) processVideo(file);
  };

  // Submit video
  const handleSubmit = async () => {
    const videoData = new FormData();
    videoData.append("file", videoFile);
    videoData.append("title", videoTitle);
    videoData.append("duration", videoDuration);
    videoData.append("is_landscape", isLandscape);

    // Always send frequency (even if empty string) to explicitly clear it when disabled
    videoData.append("frequency", frequency);

    console.log(videoData);

    const selectedLocationIds = locations.map((location) => location.ID);

    const finalCategories = isSpecialCategory
      ? [...new Set([...categories, 2])]
      : categories;

    if (finalCategories.length === 0) {
      return handleStatus(
        "Please select at least one category.",
        setToastStatus,
        setToastDescription
      );
    }

    videoData.append(
      "categories",
      JSON.stringify(finalCategories.map((cat) => cat.ID))
    );
    videoData.append("location_ids", JSON.stringify(selectedLocationIds));
    videoData.append("tag_ids", JSON.stringify(tags.map((tag) => tag.ID)));

    try {
      setIsSubmitting(true);
      setModalOpen(false);
      const result = await UpdateVideo(
        id,
        videoData,
        allLocations,
        allPartners
      );
      if (result.status !== "OK") throw new Error(result.error);
      handleStatus(
        "Video uploaded successfully!",
        setToastStatus,
        setToastDescription
      );
      navigate(-1);
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatus = (description, setStatus, setDescription) => {
    setStatus(true);
    setDescription(description);
    setTimeout(() => {
      setStatus(false);
      setDescription("");
    }, 5000);
  };

  const handleDownloadVideo = async () => {
    try {
      setIsDownloading(true);
      const { url } = await DownloadVideo(id);

      const res = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        referrerPolicy: "no-referrer",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const serverName = match ? decodeURIComponent(match[1]) : null;

      const blob = await res.blob();
      triggerDownload(
        blob,
        serverName || `${sanitize(videoTitle || "video")}.mp4`
      );

      handleStatus(
        "Video download started!",
        setToastStatus,
        setToastDescription
      );
    } catch (err) {
      try {
        const { url } = await DownloadVideo(id);
        const a = document.createElement("a");
        a.href = url;
        a.target = "_self";
        document.body.appendChild(a);
        a.click();
        a.remove();
        handleStatus(
          "Video download started (fallback)!",
          setToastStatus,
          setToastDescription
        );
      } catch {
        handleStatus(
          err?.message || "Download failed",
          setToastStatus,
          setToastDescription
        );
      }
    } finally {
      setIsDownloading(false);
    }
  };

  function triggerDownload(blob, filename) {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "video.mp4";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  }

  function sanitize(name) {
    return String(name).replace(/[^\w.-]+/g, "_");
  }

  // Handle video delete
  const handleDeleteVideo = async () => {
    try {
      setIsDeleting(true);
      setOpenDeleteModal(false);

      const content = await DeleteVideoGlobal(id);
      if (content.status !== "OK") {
        throw new Error(
          content.error || "Something went wrong, please try again later."
        );
      }

      handleStatus(
        "Video deleted successfully!",
        setToastStatus,
        setToastDescription
      );
      navigate("/videos");
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setIsDeleting(false);
    }
  };

  const initialize = async () => {
    const categoriesData = await fetchCategories();
    if (categoriesData) {
      await fetchPartners();
      await fetchLocations();
      await fetchTags();
      await fetchVideoInfo(categoriesData);
    }
  };

  useEffect(() => {
    initialize();
    return () => {
      if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (state && state.preview) setPreview(state.preview);
  }, [state]);

  return (
    <div style={{ padding: "20px", display: "flex", justifyContent: "center" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          opacity: isSubmitting ? 0.5 : 1,
          padding: "25px",
          borderRadius: "16px",
          boxShadow: "0 3px 12px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <div className="add-video-container left-container">
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              marginBottom: "20px",
              borderBottom: "2px solid #ddd",
              gap: 50,
            }}
          >
            {["Informatii generale", "Categorie & Locatii", "Taguri"].map(
              (tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    fontSize: "0.96rem",
                    fontFamily: activeTab === tab ? "SeagoeUIBold" : "SeagoeUI",
                    color: activeTab === tab ? "#000" : "#555",
                    background: "none",
                    border: "none",
                    borderBottom: activeTab === tab ? "2px solid #000" : "none",
                    cursor: "pointer",
                  }}
                >
                  {tab}
                </button>
              )
            )}
          </div>

          {activeTab === "Informatii generale" && (
            <VideoGeneralTab
              videoTitle={videoTitle}
              setVideoTitle={setVideoTitle}
              videoFile={videoFile}
              videoDuration={videoDuration}
              isLandscape={isLandscape}
              setIsLandscape={setIsLandscape}
              isSubmitting={isSubmitting}
              preview={preview}
              onDelete={() => setOpenDeleteModal(true)}
              onDownload={handleDownloadVideo}
              isDeleting={isDeleting}
              isDownloading={isDownloading}
            />
          )}
          {activeTab === "Categorie & Locatii" && (
            <CategoriesVideoTab
              categories={categories}
              setCategories={setCategories}
              availableCategories={availableCategories}
              locations={locations}
              setLocations={setLocations}
              availableLocations={availableLocations}
              partners={partners}
              setPartners={setPartners}
              availablePartners={availablePartners}
              setAllLocations={setAllLocations}
              setAllPartners={setAllPartners}
              preview={preview}
              loading={loading}
              frequency={frequency}
              setFrequency={setFrequency}
              disablePartnerEdit={categories.length > 0 && locations.length > 0}
              originalHasCategory2={isSpecialCategory}
            />
          )}
          {activeTab === "Taguri" && (
            <TagVideoTab
              tags={tags}
              setTags={setTags}
              isTagsLoading={isTagsLoading}
              availableTags={availableTags}
              handleAddTag={setTags}
              preview={preview}
            />
          )}
          {toastStatus && <PopSnackbar message={toastDescription} />}
        </div>
        <div className="add-video-container right-container">
          {/* <h1 className="add-video-title left">Incarca spot</h1> */}
          {/* {!preview && (
            <div className="upload-area">
              <StyledDropzone
                multiple={false}
                onDrop={handleFileChange}
                title={"Drag 'n' drop sau click pentru a incarca spotul"}
                accept="video/*"
              />
            </div>
          )} */}
          {videoPreview && (
            <div className="video-preview">
              <video key={videoPreview} width="100%" controls>
                <source src={videoPreview} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: "30px",
          borderTop: "1px solid rgba(0, 0, 0, 0.06)",
          zIndex: 1000,
          display: "flex",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        <button
          disabled={isSubmitting}
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f9fa",
            color: "#495057",
            padding: "14px 32px",
            border: "2px solid #e9ecef",
            borderRadius: "8px",
            fontFamily: "SeagoeUIBold",
            cursor: "pointer",
            fontSize: "1em",
            minWidth: "180px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#e9ecef";
            e.target.style.borderColor = "#dee2e6";
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#f8f9fa";
            e.target.style.borderColor = "#e9ecef";
            e.target.style.transform = "translateY(0px)";
            e.target.style.boxShadow = "none";
          }}
        >
          Anuleaza
        </button>
        <button
          onClick={preview ? () => navigate(-1) : onSubmitClick}
          disabled={
            !videoFile ||
            !videoTitle ||
            !videoDuration ||
            categories.length === 0 ||
            isSubmitting ||
            (categories.some((c) => c.ID === 2) &&
              (partners.length === 0 ||
                (!allPartners && locations.length === 0)))
          }
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              isSubmitting ||
              !videoFile ||
              !videoTitle ||
              !videoDuration ||
              categories.length === 0 ||
              (categories.some((c) => c.ID === 2) &&
                (partners.length === 0 ||
                  (!allPartners && locations.length === 0)))
                ? "#ccc"
                : "#000",
            color: "white",
            fontFamily: "SeagoeUIBold",
            padding: "14px 32px",
            border: "none",
            borderRadius: "8px",
            cursor:
              isSubmitting ||
              !videoFile ||
              !videoTitle ||
              !videoDuration ||
              categories.length === 0 ||
              (categories.some((c) => c.ID === 2) &&
                (partners.length === 0 ||
                  (!allPartners && locations.length === 0)))
                ? "not-allowed"
                : "pointer",
            fontSize: "1em",
            minWidth: "200px",
            opacity:
              isSubmitting ||
              !videoFile ||
              !videoTitle ||
              !videoDuration ||
              categories.length === 0 ||
              (categories.some((c) => c.ID === 2) &&
                (partners.length === 0 ||
                  (!allPartners && locations.length === 0)))
                ? 0.7
                : 1,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow:
              isSubmitting ||
              !videoFile ||
              !videoTitle ||
              !videoDuration ||
              categories.length === 0 ||
              (categories.some((c) => c.ID === 2) &&
                (partners.length === 0 ||
                  (!allPartners && locations.length === 0)))
                ? "none"
                : "0 4px 14px rgba(0, 0, 0, 0.15)",
          }}
          onMouseEnter={(e) => {
            if (
              !isSubmitting &&
              videoFile &&
              videoTitle &&
              videoDuration &&
              categories.length > 0 &&
              (!categories.some((c) => c.ID === 2) ||
                (partners.length > 0 && (allPartners || locations.length > 0)))
            ) {
              e.target.style.backgroundColor = "#1e3a8a";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 25px rgba(30, 58, 138, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            if (
              !isSubmitting &&
              videoFile &&
              videoTitle &&
              videoDuration &&
              categories.length > 0 &&
              (!categories.some((c) => c.ID === 2) ||
                (partners.length > 0 && (allPartners || locations.length > 0)))
            ) {
              e.target.style.backgroundColor = "#000";
              e.target.style.transform = "translateY(0px)";
              e.target.style.boxShadow = "0 4px 14px rgba(0, 0, 0, 0.15)";
            }
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : preview ? (
            "Inapoi"
          ) : (
            "Salveaza modificarile"
          )}
        </button>
      </div>
      <AlertModal
        text={`Ai selectat ${partners[0]?.name} ca partener pentru acest spot. Esti sigur ca vrei sa adaugi acest spot?`}
        open={partners.length === 1 && modalOpen}
        showYesButton={true}
        handleYes={handleSubmit}
        showNoButton={true}
        showOkButton={false}
        handleClose={handleModalClose}
      />
      <AlertModal
        open={openDeleteModal}
        text={
          <>
            <p style={{ fontFamily: "SeagoeUIBold" }}>
              Esti sigur ca vrei sa stergi acest spot?
            </p>
            <br />
            Toate playlist-urile dispozitivelor care contin acest spot vor fi
            regenerate dupa stergere.
          </>
        }
        handleClose={() => setOpenDeleteModal(false)}
        handleYes={handleDeleteVideo}
        showYesButton={true}
        showNoButton={true}
        showOkButton={false}
      />
    </div>
  );
};

export default EditVideo;
